import React, { useContext, useEffect, useState } from "react";
import { Button, Checkbox, Input, message, Table } from "antd";
import dayjs from "dayjs";
import { updateForecastedPlots } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import ForecastSummaryTable from "./ForecastSummaryTable";
import { cloneDeep } from "lodash";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";
import { inchesToCm } from "../../../../utils/cmToInchesConversion";

const ConfigTable = ({
  forecastedData,
  setForecastData,
  currentOrg,
  currentSite,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  currentDate,
  siteMetadata,
  totalActualHeadCount,
  specData,
  setSpecData,
  isImperial = false,
}) => {
  const [tableData, setTableData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [maximumHarvestPerWeek, setMaximumHarvestPerWeek] = useState(2);
  const [minimumSizeToStart, setMinimumSizeToStart] = useState(3);

  const [filteredTableData, setFilteredTableData] = useState([]);

  const { userData } = useContext(UserContext);
  const [messageApi, contextHolder] = message.useMessage();

  // -------------------------------------------------------------------------
  // 1) Flatten forecast_harvest_data into table rows
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!forecastedData || !forecastedData.forecast_harvest_data) {
      setTableData([]);
      return;
    }

    forecastedData.forecast_harvest_data.sort((a, b) =>
      dayjs(a.forecast_date, BACKEND_DATE_FORMAT).diff(
        dayjs(b.forecast_date, BACKEND_DATE_FORMAT),
      ),
    );

    const rows = forecastedData.forecast_harvest_data.map((item, idx) => ({
      ...item.data,
      key: item?.data?.key || `row-${idx}`,
      forecast_day: dayjs(item.forecast_date, BACKEND_DATE_FORMAT).format("ddd").toUpperCase(),
      forecast_date: item.forecast_date,
      harvested_date: item.data?.harvested_date || "",
      quality_reduction_factor: item.data?.quality_reduction_factor ?? 0,
      sizes: item.data?.sizes || [],
    }));

    setTableData(rows);
  }, [forecastedData]);

  // -------------------------------------------------------------------------
  // 2) Build the "Spec Table" data from siteMetadata
  //    and read the current values from the *first* forecast row (or any row)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const crateConfig = siteMetadata?.FORECAST?.crate_config || [];

    if (siteMetadata?.FORECAST?.maximum_harvest_per_week) {
      setMaximumHarvestPerWeek(
        siteMetadata?.FORECAST?.maximum_harvest_per_week,
      );
    }

    if (siteMetadata?.FORECAST?.minimum_size_to_start) {
      setMinimumSizeToStart(siteMetadata?.FORECAST?.minimum_size_to_start);
    }

    if (!crateConfig.length) {
      setSpecData([]);
      return;
    }

    const firstRow = tableData?.[0] || {};

    const newSpecData = crateConfig.map((specItem, idx) => {
      const {
        label,
        lower_bound_key_name,
        upper_bound_key_name,
        buffer_lower_bound_key_name,
        buffer_upper_bound_key_name,
        divide_by_number,
        lower_buffer_logic,
        upper_buffer_logic,
        graph_band_color,
      } = specItem;

      return {
        key: `spec-${idx}`,
        label,

        [lower_bound_key_name]: firstRow[lower_bound_key_name] ?? specItem[lower_bound_key_name] ?? 0,
        [upper_bound_key_name]: firstRow[upper_bound_key_name] ?? specItem[upper_bound_key_name] ?? 0,
        [buffer_lower_bound_key_name]:
          firstRow[buffer_lower_bound_key_name] ?? specItem[buffer_lower_bound_key_name] ?? 0,
        [buffer_upper_bound_key_name]:
          firstRow[buffer_upper_bound_key_name] ?? specItem[buffer_upper_bound_key_name] ?? 0,

        lower_bound_key_name,
        upper_bound_key_name,
        buffer_lower_bound_key_name,
        buffer_upper_bound_key_name,
        divide_by_number,
        lower_buffer_logic,
        upper_buffer_logic,
        graph_band_color,
      };
    });

    setSpecData(newSpecData);
  }, [siteMetadata, tableData]);

  useEffect(() => {
    if (!tableData || !tableData.length || !specData || !specData.length)
      return;
    const filteredTableData = cloneDeep(tableData);
    filteredTableData.sort((a, b) =>
      dayjs(a.forecast_date, BACKEND_DATE_FORMAT).diff(
        dayjs(b.forecast_date, BACKEND_DATE_FORMAT),
      ),
    );

    // find finalLowerBound and finalUpperBound across all the spec size and take lowest Lower Bound and highest Upper Bound across all spec size.
    let lowestLowerBound = Infinity;
    let highestUpperBound = -Infinity;

    specData.forEach((spec) => {
      const lbVal =
        parseFloat(filteredTableData[0][spec.lower_bound_key_name]) || 0;
      const ubVal =
        parseFloat(filteredTableData[0][spec.upper_bound_key_name]) || 0;

      // Convert bounds to cm for filtering
      // Imperial: bounds are in inches → cm (×2.54)
      // Metric: already in cm
      const lbScaled = isImperial ? inchesToCm(lbVal) : lbVal;
      const ubScaled = isImperial ? inchesToCm(ubVal) : ubVal;

      // Apply buffer logic for finalLower
      let finalLower;
      if (spec.lower_buffer_logic === "sub") {
        finalLower = lbScaled - (spec[spec.buffer_lower_bound_key_name] || 0);
      } else {
        finalLower = lbScaled + (spec[spec.buffer_lower_bound_key_name] || 0);
      }

      // Apply buffer logic for finalUpper
      let finalUpper;
      if (spec.upper_buffer_logic === "sub") {
        finalUpper = ubScaled - (spec[spec.buffer_upper_bound_key_name] || 0);
      } else {
        finalUpper = ubScaled + (spec[spec.buffer_upper_bound_key_name] || 0);
      }

      const lower = Math.min(finalLower, finalUpper);
      const upper = Math.max(finalLower, finalUpper);

      if (lower < lowestLowerBound) {
        lowestLowerBound = lower;
      }
      if (upper > highestUpperBound) {
        highestUpperBound = upper;
      }
    });

    filteredTableData.forEach((forecast, index) => {
      if (forecast.harvested_date !== "") {
        forecast.harvested_sizes = cloneDeep(forecast.sizes || []);
        // get all indexes where element is in between the lowestLowerBound <= x > highestUpperBound
        const indexesToZero = [];
        forecast.sizes.forEach((val, idx) => {
          if (val >= lowestLowerBound && val < highestUpperBound) {
            indexesToZero.push(idx);
          }
        });
        // start loop from current index to last index of tableData and update forecast.sizes[ind] = 0 for all ind in filtered indexes.
        for (let j = index; j < filteredTableData.length; j++) {
          indexesToZero.forEach((k) => {
            filteredTableData[j].sizes[k] = 0;
          });
        }
      } else {
        forecast.harvested_sizes = [];
      }
    });
    setFilteredTableData(filteredTableData);
  }, [tableData, specData, isImperial]);


  // -------------------------------------------------------------------------
  // COLUMNS for the FORECAST table
  // -------------------------------------------------------------------------
  const forecastColumns = [
    {
      title: "Forecast Day",
      dataIndex: "forecast_day",
      key: "forecast_day",
      fixed: "left",
      width: 150,
    },
    {
      title: "Forecast Date",
      dataIndex: "forecast_date",
      key: "forecast_date",
      fixed: "left",
      width: 150,
      render: (value) => (value ? dayjs(value).format(UI_DATE_FORMAT) : ""),
    },
    {
      // Replaces date picker with a checkbox
      title: "Harvested?",
      dataIndex: "harvested_date",
      key: "harvested_date",
      align: "center",
      width: 110,
      render: (value, record) => {
        const isChecked = record.harvested_date === record.forecast_date;
        return (
          <Checkbox
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                // If checked => harvested_date = forecast_date
                onForecastCellEdit(
                  record.key,
                  "harvested_date",
                  record.forecast_date,
                );
              } else {
                // If unchecked => harvested_date = ""
                onForecastCellEdit(record.key, "harvested_date", "");
              }
            }}
          >
            {/* Optional label text, or leave blank if you prefer */}
            {/*{isChecked*/}
            {/*  ? `Harvested on ${dayjs(record.forecast_date).format(UI_DATE_FORMAT)}`*/}
            {/*  : "Not Harvested"}*/}
          </Checkbox>
        );
      },
    },
    {
      title: "Quality Reduction Factor",
      dataIndex: "quality_reduction_factor",
      key: "quality_reduction_factor",
      width: 190,
      render: (value, record) => (
        <Input
          type="number"
          min={0}
          step={0.01}
          className="w-32 md:w-28 sm:w-24"
          value={value}
          onChange={(e) =>
            onForecastCellEdit(
              record.key,
              "quality_reduction_factor",
              e.target.value,
            )
          }
        />
      ),
    },
  ];

  // -------------------------------------------------------------------------
  // COLUMNS for the SPEC table (only show buffer columns if user is superuser)
  // -------------------------------------------------------------------------
  const specColumns = [
    {
      title: "Spec Sizes",
      dataIndex: "label",
      key: "label",
      width: 150,
      fixed: "left",
    },
    {
      title: "Lower Bound",
      dataIndex: "lower_bound_key_name",
      key: "lower_bound_key_name",
      width: 150,
      render: (unused, record) => {
        const { lower_bound_key_name } = record;
        const value = record[lower_bound_key_name];
        return (
          <Input
            type="number"
            min={0}
            step={isImperial ? 0.5 : 1}
            style={{ width: "100%" }}
            value={Number.isFinite(Number(value)) ? String(value) : ""}
            onChange={(e) => {
              const newVal = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
              onSpecCellEdit(
                record.key,
                lower_bound_key_name,
                newVal,
              );
            }}
          />
        );
      },
    },
    {
      title: "Upper Bound",
      dataIndex: "upper_bound_key_name",
      key: "upper_bound_key_name",
      width: 150,
      render: (unused, record) => {
        const { upper_bound_key_name } = record;
        const value = record[upper_bound_key_name];
        return (
          <Input
            type="number"
            min={0}
            step={isImperial ? 0.5 : 1}
            style={{ width: "100%" }}
            value={Number.isFinite(Number(value)) ? String(value) : ""}
            onChange={(e) => {
              const newVal = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
              onSpecCellEdit(
                record.key,
                upper_bound_key_name,
                newVal,
              );
            }}
          />
        );
      },
    },
    ...(userData?.is_superuser
      ? [
          {
            title: "Buffer Lower Bound",
            dataIndex: "buffer_lower_bound_key_name",
            key: "buffer_lower_bound_key_name",
            width: 170,
            render: (unused, record) => {
              const { buffer_lower_bound_key_name } = record;
              const value = record[buffer_lower_bound_key_name];
              return (
                <Input
                  type="number"
                  min={0}
                  step={isImperial ? 0.5 : 1}
                  style={{ width: "100%" }}
                  value={Number.isFinite(Number(value)) ? String(value) : ""}
                  onChange={(e) => {
                    const newVal = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
                    onSpecCellEdit(
                      record.key,
                      buffer_lower_bound_key_name,
                      newVal,
                    );
                  }}
                />
              );
            },
          },
          {
            title: "Buffer Upper Bound",
            dataIndex: "buffer_upper_bound_key_name",
            key: "buffer_upper_bound_key_name",
            width: 170,
            render: (unused, record) => {
              const { buffer_upper_bound_key_name } = record;
              const value = record[buffer_upper_bound_key_name];
              return (
                <Input
                  type="number"
                  min={0}
                  step={isImperial ? 0.5 : 1}
                  style={{ width: "100%" }}
                  value={Number.isFinite(Number(value)) ? String(value) : ""}
                  onChange={(e) => {
                    const newVal = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
                    onSpecCellEdit(
                      record.key,
                      buffer_upper_bound_key_name,
                      newVal,
                    );
                  }}
                />
              );
            },
          },
        ]
      : []),
  ];

  // -------------------------------------------------------------------------
  // 3) Handlers to update local state
  // -------------------------------------------------------------------------
  // Update forecast row
  const onForecastCellEdit = (rowKey, fieldName, newVal) => {
    if (fieldName === "harvested_date" && newVal !== "") {
      let count = 0;
      tableData.map((row) => {
        if (row.harvested_date === row.forecast_date) {
          count++;
        }
      });
      if (count >= maximumHarvestPerWeek) {
        messageApi.error(
          `Maximum number of harvests per week is ${maximumHarvestPerWeek}`,
        );
        return;
      }
    }

    setTableData((prev) =>
      prev.map((row) =>
        row.key === rowKey ? { ...row, [fieldName]: newVal } : row,
      ),
    );
  };

  // Update spec row => also apply to *all* forecast rows for that key
  const onSpecCellEdit = (rowKey, specKeyName, newVal) => {
    const parsed = newVal == null ? null : Number(newVal);
    const safeVal = Number.isFinite(parsed) ? parsed : null;
    setSpecData((prev) =>
      prev.map((row) => {
        if (row.key === rowKey) {
          return {
            ...row,
            [specKeyName]: safeVal,
          };
        }
        return row;
      }),
    );

    setTableData((prevForecast) =>
      prevForecast.map((fr) => ({
        ...fr,
        [specKeyName]: safeVal,
      })),
    );
  };

  // -------------------------------------------------------------------------
  // 4) "Save All" => transform back into the shape your backend expects,
  //    then call updateForecastedPlots(...) with that data.
  // -------------------------------------------------------------------------
  const handleSaveAll = async () => {
    setLoading(true);

    const greenhouseName = currentGreenhouse?.name || "UnknownGreenhouse";

    const reconData = {
      [greenhouseName]: [
        {
          greenhouse_id: currentGreenhouse?.id || "",
          plot_id: currentPlot?.id || "",
          plot_name: currentPlot?.name || "",
          forecasts: tableData.map((row) => ({
            ...row,
            date: row.forecast_date,
            sizes: row.sizes || [],
            harvested_date: row.harvested_date || "",
            quality_reduction_factor:
              parseFloat(row.quality_reduction_factor) || 0,
          })),
        },
      ],
    };

    try {
      const response = await updateForecastedPlots(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentDate,
        reconData,
      );

      if (response.status === 200) {
        messageApi.success("All data saved successfully!");
        setForecastData(response?.data?.data?.[0] || []);
      } else {
        messageApi.error("Error saving data.");
      }
    } catch (err) {
      console.error("Save error:", err);
      messageApi.error("Error saving data.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  return (
    <div className="w-full">
      {contextHolder}

      <div className="mb-4 flex items-center justify-between gap-3 md:flex-col md:items-stretch">
        <h2 className="text-lg font-semibold">Config Table</h2>
        <Button
          type="primary"
          onClick={handleSaveAll}
          loading={loading}
          className="w-auto md:w-full"
        >
          Save All
        </Button>
      </div>

      {/* Spec Sizes & Forecast Table side-by-side (or stacked on small screens) */}
      <div className="mb-4 flex w-full flex-row gap-4 lg:flex-col">
        {/* Spec Size Table */}
        <div className="config-table-section flex w-full min-w-0 flex-1 flex-col items-center justify-start rounded-md shadow-md transition-shadow duration-300 hover:shadow-2xl">
          <Table
            dataSource={specData}
            className="w-full"
            columns={specColumns}
            rowKey="key"
            pagination={false}
            scroll={{ x: 800 }}
          />
        </div>

        {/* Forecast Harvest Table */}
        <div className="config-table-section flex w-full min-w-0 flex-1 flex-col items-center justify-center rounded-md shadow-md transition-shadow duration-300 hover:shadow-2xl">
          <Table
            dataSource={tableData}
            className="w-full"
            columns={forecastColumns}
            rowKey="key"
            pagination={false}
            scroll={{ x: 460 }}
          />
        </div>
      </div>

      {/* Forecast Summary Table with the new Harvested Yield column */}
      <ForecastSummaryTable
        tableData={filteredTableData}
        specs={specData}
        totalActualHeadCount={totalActualHeadCount}
        minimumSizeToStart={minimumSizeToStart}
        isImperial={isImperial}
      />
    </div>
  );
};

export default ConfigTable;
